import { parseSerials } from '@ims/core'

// A camera reports the code in view several times a second. These decide what
// counts as one scan; the screen and the camera itself are in scanner.tsx and
// the platform.

/**
 * A code counts again once it has been out of view this long: take the pack
 * away and show the next one. While it stays in view it is one scan, however
 * long it is held there.
 */
export const OUT_OF_VIEW_MS = 800

/** The code the camera saw last, and when it last saw it. */
export interface LastScan {
  code: string
  atMs: number
}

/** A code as it should be looked up: no spaces around it, no control characters. */
export function cleanCode(raw: string): string {
  // eslint-disable-next-line no-control-regex
  return raw.replace(/[\u0000-\u001f\u007f]/g, '').trim()
}

/**
 * Whether a read is a new scan: a different code, or the same one coming back
 * after it left the view. The caller records every read (accepted or not) as
 * `previous`, so `atMs` is when the code was last seen.
 */
export function acceptScan(previous: LastScan | null, code: string, nowMs: number): boolean {
  if (!code) return false
  return !previous || previous.code !== code || nowMs - previous.atMs >= OUT_OF_VIEW_MS
}

/**
 * A scanned serial added to a box that lists one per line. Returns the new text
 * and how many it lists, or null when that serial is already in the list.
 */
export function addScannedSerial(
  text: string,
  code: string,
): { text: string; count: number } | null {
  const before = parseSerials(text).serials
  const after = parseSerials(`${text}\n${code}`).serials
  return after.length === before.length ? null : { text: after.join('\n'), count: after.length }
}

// -----------------------------------------------------------------------------
// The picture: which part of it is read, how far it is zoomed, which camera.
// -----------------------------------------------------------------------------

/** A box as fractions (0 to 1) of the picture the person sees. */
export interface ScanRegion {
  x: number
  y: number
  w: number
  h: number
}

/**
 * The guide box: a square in the middle of the 4:3 camera view (its width is 0.62 of the
 * view's height), the finder shopkeepers know from other billing apps. A long barcode that
 * does not fit is still read by the whole-view pass.
 */
export const GUIDE_BOX: ScanRegion = { x: 0.2675, y: 0.12, w: 0.465, h: 0.62 }
export const WHOLE_VIEW: ScanRegion = { x: 0, y: 0, w: 1, h: 1 }

/**
 * A region of the visible picture, in the video's own pixels. The view crops
 * the stream to fill its box (`object-cover`: a 16:9 stream in a 4:3 view
 * loses its sides), so "the middle of what I see" is not the middle strip of
 * the frame. Reading these exact pixels, never a scaled copy, is what lets a
 * tiny barcode keep enough pixels per bar.
 */
export function videoRegion(
  videoW: number,
  videoH: number,
  boxW: number,
  boxH: number,
  region: ScanRegion,
): { sx: number; sy: number; sw: number; sh: number } | null {
  if (videoW <= 0 || videoH <= 0 || boxW <= 0 || boxH <= 0) return null
  const scale = Math.max(boxW / videoW, boxH / videoH)
  const visibleW = boxW / scale
  const visibleH = boxH / scale
  const left = (videoW - visibleW) / 2
  const top = (videoH - visibleH) / 2
  const sx = Math.max(0, Math.round(left + region.x * visibleW))
  const sy = Math.max(0, Math.round(top + region.y * visibleH))
  return {
    sx,
    sy,
    sw: Math.min(videoW - sx, Math.round(region.w * visibleW)),
    sh: Math.min(videoH - sy, Math.round(region.h * visibleH)),
  }
}

/**
 * Zoom opens here. Not zoomed: a lens focuses on a pack held 15 to 20 cm away, and zooming in
 * first only tempts people to hold it closer than that (which is how the first try went).
 */
export const START_ZOOM = 1

/** The zoom buttons to offer: 1×, 2× and 3× within what the camera can do (none when it cannot zoom). */
export function zoomSteps(min: number, max: number): number[] {
  const steps = [1, 2, 3].filter((z) => z >= min - 0.01 && z <= max + 0.01)
  return steps.length > 1 ? steps : []
}

export function clampZoom(level: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, level))
}

/** The zoom after two fingers moved from `startDistance` apart to `distance` apart. */
export function pinchZoom(
  startZoom: number,
  startDistance: number,
  distance: number,
  min: number,
  max: number,
): number {
  if (startDistance <= 0) return clampZoom(startZoom, min, max)
  return clampZoom((startZoom * distance) / startDistance, min, max)
}

export interface CameraChoice {
  id: string
  label: string
  /**
   * What the lens can do, once it has been tried (see `needsTrying`). A phone's
   * labels rarely say which lens is which ("camera2 0, facing back"), but a lens
   * that cannot refocus is no use for a barcode held close.
   */
  autofocus?: boolean
  /** The phone's light works with this lens (it does with the main one). */
  light?: boolean
}

/** A lens that is poor at small barcodes: it shows too much, or cannot focus at arm's length. */
const POOR_LENS = /ultra|wide|tele|macro|depth/i

/** How good a lens is for a barcode held close: refocusing matters most, then the light. */
function lensScore(camera: CameraChoice): number {
  return (
    (camera.autofocus ? 4 : 0) + (camera.light ? 2 : 0) + (POOR_LENS.test(camera.label) ? 0 : 1)
  )
}

/**
 * Whether the back cameras should each be tried once to learn what they can do:
 * there is a choice to make, and this device has not made it before.
 */
export function needsTrying(
  cameras: readonly CameraChoice[],
  rememberedId: string | null,
): boolean {
  return cameras.length > 1 && !cameras.some((c) => c.id === rememberedId)
}

/**
 * Which back camera to scan with. The one this device settled on before, if it
 * is still there. Otherwise the most able one where abilities are known (the
 * first of equals, which is the browser's own order); where they are not, the
 * browser's choice stays unless its label says it is a wide, tele or macro lens.
 */
export function pickCamera(
  cameras: readonly CameraChoice[],
  rememberedId: string | null,
  currentId: string | null,
): string | null {
  if (rememberedId && cameras.some((c) => c.id === rememberedId)) return rememberedId
  const current = cameras.find((c) => c.id === currentId)
  if (cameras.some((c) => c.autofocus !== undefined)) {
    let best = current ?? cameras[0]
    for (const camera of cameras) {
      if (best && lensScore(camera) > lensScore(best)) best = camera
    }
    return best?.id ?? null
  }
  if (current && !POOR_LENS.test(current.label)) return current.id
  return cameras.find((c) => !POOR_LENS.test(c.label))?.id ?? current?.id ?? cameras[0]?.id ?? null
}

/** The camera after `currentId`, going round. */
export function nextCameraId(
  cameras: readonly CameraChoice[],
  currentId: string | null,
): string | null {
  if (cameras.length === 0) return null
  const at = cameras.findIndex((c) => c.id === currentId)
  return cameras[(at + 1) % cameras.length]!.id
}
